import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MunicipalitySelect } from "@/components/MunicipalitySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSortableData, SortableHead } from "@/hooks/useSortableData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { ExportButton } from "@/components/ExportButton";

const EMPTY = { date: "", ward_no: "", sweeping_type: "", total_road_area: "", road_area_covered: "" };

export default function RoadSweeping() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [muni, setMuni] = useState({ district: "", name: "", type: "" });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const canManage = hasPerm("road_sweeping.manage");

  const load = () => {
    setLoading(true);
    api.get("/road-sweeping").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter((it) => [it.district, it.municipality_name, it.municipality_type, it.ward_no, it.sweeping_type].some((v) => (v || "").toString().toLowerCase().includes(q)));
  }, [items, search]);

  const livePct = useMemo(() => {
    const t = Number(form.total_road_area), c = Number(form.road_area_covered);
    return t > 0 ? Math.round((c / t) * 10000) / 100 : 0;
  }, [form.total_road_area, form.road_area_covered]);

  const openNew = () => { setForm(EMPTY); setMuni({ district: "", name: "", type: "" }); setEditing({}); };
  const openEdit = (it) => {
    setForm({ date: it.date || "", ward_no: it.ward_no ?? "", sweeping_type: it.sweeping_type || "", total_road_area: it.total_road_area ?? "", road_area_covered: it.road_area_covered ?? "" });
    setMuni({ district: it.district || "", name: it.municipality_name || "", type: it.municipality_type || "" });
    setEditing(it);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!muni.district || !muni.name) { toast.error("Select district and municipal name"); return; }
    setSaving(true);
    const payload = {
      district: muni.district,
      municipality_name: muni.name,
      municipality_type: muni.type,
      date: form.date || null,
      ward_no: form.ward_no === "" ? null : Number(form.ward_no),
      sweeping_type: form.sweeping_type || null,
      total_road_area: form.total_road_area === "" ? null : Number(form.total_road_area),
      road_area_covered: form.road_area_covered === "" ? null : Number(form.road_area_covered),
    };
    try {
      if (editing && editing.id) await api.put(`/road-sweeping/${editing.id}`, payload);
      else await api.post("/road-sweeping", payload);
      toast.success(editing && editing.id ? "Updated" : "Added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/road-sweeping/${deleteItem.id}`); toast.success("Deleted"); setDeleteItem(null); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const num = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN"));

  const sort = useSortableData(filtered);
  const sorted = sort.sorted;

  return (
    <div data-testid="road-sweeping-page">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Monitoring</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Road Sweeping</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} record(s) of road sweeping coverage.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton items={items} filename="road-sweeping" testId="road-sweeping-export-btn" />
          {canManage && (
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="road-sweeping-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Add New
          </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by district, municipality, ward, type..." className="pl-9" data-testid="road-sweeping-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">S.No</TableHead>
                <SortableHead column="district" sort={sort}>District</SortableHead>
                <SortableHead column="municipality_name" sort={sort}>Municipality</SortableHead>
                <SortableHead column="municipality_type" sort={sort}>Type</SortableHead>
                <SortableHead column="date" sort={sort}>Date</SortableHead>
                <SortableHead column="ward_no" sort={sort} className="text-right" right>Ward</SortableHead>
                <SortableHead column="sweeping_type" sort={sort}>Sweeping Type</SortableHead>
                <SortableHead column="total_road_area" sort={sort} className="text-right" right>Total Road Area (km)</SortableHead>
                <SortableHead column="road_area_covered" sort={sort} className="text-right" right>Area Covered (km)</SortableHead>
                <SortableHead column="percentage" sort={sort} className="text-right" right>Percentage</SortableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={canManage ? 11 : 10} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 11 : 10} className="text-center py-12">
                    <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No road sweeping records yet.</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((it, idx) => (
                  <TableRow key={it.id} className="hover:bg-slate-50" data-testid={`road-sweeping-row-${it.id}`}>
                    <TableCell className="text-slate-500">{idx + 1}</TableCell>
                    <TableCell className="capitalize">{(it.district || "—").toLowerCase()}</TableCell>
                    <TableCell className="font-medium">{it.municipality_name || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{it.municipality_type || "—"}</TableCell>
                    <TableCell>{it.date ? new Date(it.date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-right">{it.ward_no ?? "—"}</TableCell>
                    <TableCell>{it.sweeping_type || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(it.total_road_area)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(it.road_area_covered)}</TableCell>
                    <TableCell className="text-right">
                      <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 font-semibold px-2.5 py-1 text-xs">{it.percentage != null ? `${it.percentage}%` : "—"}</span>
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`road-sweeping-edit-${it.id}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteItem(it)} data-testid={`road-sweeping-delete-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="road-sweeping-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing && editing.id ? "Edit Road Sweeping Record" : "Add Road Sweeping Record"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <MunicipalitySelect value={muni} onChange={setMuni} testId="road-sweeping-muni" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="road-sweeping-input-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Ward Number</Label>
                <Input type="number" value={form.ward_no} onChange={(e) => setField("ward_no", e.target.value)} data-testid="road-sweeping-input-ward" />
              </div>
              <div className="space-y-1.5">
                <Label>Type of Sweeping</Label>
                <Select value={form.sweeping_type} onValueChange={(v) => setField("sweeping_type", v)}>
                  <SelectTrigger data-testid="road-sweeping-input-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Machine">Machine</SelectItem>
                    <SelectItem value="Manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Total Road Area (km)</Label>
                <Input type="number" step="0.01" value={form.total_road_area} onChange={(e) => setField("total_road_area", e.target.value)} data-testid="road-sweeping-input-total" />
              </div>
              <div className="space-y-1.5">
                <Label>Total Road Area Covered (km)</Label>
                <Input type="number" step="0.01" value={form.road_area_covered} onChange={(e) => setField("road_area_covered", e.target.value)} data-testid="road-sweeping-input-covered" />
              </div>
              <div className="space-y-1.5">
                <Label>Percentage (auto)</Label>
                <Input value={`${livePct}%`} readOnly className="bg-slate-50 font-semibold text-emerald-700" data-testid="road-sweeping-input-percentage" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="road-sweeping-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="road-sweeping-save-btn">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this road sweeping record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="road-sweeping-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="road-sweeping-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
